"use client"

import { useState } from "react"
import Link from "next/link"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { Menu, X, ArrowRight, User } from "lucide-react"

import { Button } from "@/components/ui/button"
import { MorphingHeader } from "@/components/ui/morphing-header"
import { cn } from "@/lib/utils"
import { CartLink } from "./cart-link"

export type NavLink = { href: string; label: string; highlight?: boolean | null }

const DEFAULT_NAV_LINKS: NavLink[] = [
  { href: "/", label: "Accueil" },
  { href: "/affutage", label: "Affûtage" },
  { href: "/outillage", label: "Outillage" },
  { href: "/maintenance", label: "Maintenance" },
  { href: "/actualites", label: "Actualités" },
  { href: "/qui-sommes-nous", label: "Qui sommes nous" },
]

// Mobile-only slide-out additions. Empty by default.
const DEFAULT_MOBILE_ONLY: NavLink[] = []

export type SiteHeaderProps = {
  /** Primary nav, visible on desktop. Defaults to the hardcoded ADIS 95 menu. */
  navLinks?: NavLink[]
  /** Links that appear only in the mobile slide-out menu. */
  mobileOnlyLinks?: NavLink[]
  /** Brand wordmark shown next to the logo. */
  brandName?: string
}

export function SiteHeader({
  navLinks = DEFAULT_NAV_LINKS,
  mobileOnlyLinks = DEFAULT_MOBILE_ONLY,
  brandName = "ADIS 95",
}: SiteHeaderProps = {}) {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)

  // Test alternative landing provides its own chrome — hide the default header.
  // Same for /audit, which uses its own sticky AuditNav and a print-friendly
  // layout designed for a confidential client document.
  if (pathname?.startsWith("/test-alt-landing")) return null
  if (pathname?.startsWith("/audit")) return null

  // Primary links shown on desktop.
  const desktopNav = navLinks.filter((l) => !l.highlight)

  return (
    <>
      <MorphingHeader
        threshold={24}
        trailing={
          <>
            {/* Compte + Panier — visible on all sizes. */}
            <Link
              href="/mon-compte"
              aria-label="Mon compte"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              <User className="size-5" />
            </Link>
            <CartLink />

            {/* Desktop CTA — Contact. Hidden once the header is
                scrolled so the smaller pill (max-w-3xl) doesn't have
                to fight 6 nav links + 2 icons + a button + the
                hamburger trigger for ~640px of horizontal space. The
                user can still reach /contact via the nav link or the
                page-level CTA banners. */}
            <Button
              asChild
              size="sm"
              className={cn(
                "hidden rounded-full lg:inline-flex",
                "active:scale-[0.97] transition-[opacity,transform,background-color] duration-200",
                "[[data-scrolled=true]_&]:lg:hidden",
              )}
            >
              <Link href="/contact" className="group">
                Contact
                <ArrowRight className="ml-1 size-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
            </Button>

            {/* Mobile hamburger. */}
            <Button
              variant="ghost"
              size="icon-lg"
              className="min-h-[44px] min-w-[44px] lg:hidden active:scale-[0.97]"
              aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={open}
              onClick={() => setOpen(!open)}
            >
              {open ? <X className="size-5" /> : <Menu className="size-5" />}
            </Button>
          </>
        }
      >
        <MorphingHeader.Brand
          text={brandName}
          href="/"
          logo={
            <Image
              src="/images/logo-adis95-modern.svg"
              alt=""
              aria-hidden="true"
              width={28}
              height={28}
              className="size-7 object-contain"
              priority
            />
          }
        />

        <MorphingHeader.Nav>
          {desktopNav.map((link, i) => (
            <MorphingHeader.NavLink
              key={link.href}
              href={link.href}
              index={i}
              exact={link.href === "/"}
            >
              {link.label}
            </MorphingHeader.NavLink>
          ))}
        </MorphingHeader.Nav>
      </MorphingHeader>

      {/* Spacer — réserve la hauteur de la pill au repos pour que le
          contenu de page (Hero carousel, images, etc.) ne passe jamais
          sous le header fixe. Rendu uniquement quand le header lui-même
          l'est : sur /audit et /test-alt-landing on a déjà fait
          `return null` plus haut, donc pas de padding parasite. */}
      <MorphingHeader.Spacer />

      {/* Mobile menu overlay — preserved verbatim from the previous
          implementation (full-screen sheet with staggered fade-in). */}
      {open && (
        <div
          className="fixed inset-0 z-[60] bg-background lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Menu de navigation"
        >
          <div className="flex items-center justify-between px-6 pt-6">
            <Link
              href="/"
              onClick={() => setOpen(false)}
              className="flex items-center"
            >
              <Image
                src="/images/logo-adis95-modern.svg"
                alt=""
                aria-hidden="true"
                width={28}
                height={28}
                className="size-7 object-contain"
              />
              <span
                className="ml-1.5 text-sm font-extrabold tracking-tight text-primary"
                aria-hidden="true"
              >
                {brandName}
              </span>
            </Link>
            <Button
              variant="ghost"
              size="icon-lg"
              onClick={() => setOpen(false)}
              className="min-h-[44px] min-w-[44px] active:scale-[0.97]"
              aria-label="Fermer le menu"
            >
              <X className="size-5" />
            </Button>
          </div>

          <div className="flex flex-1 flex-col justify-center gap-2 px-6 pt-12">
            {[...navLinks, ...mobileOnlyLinks].map((link, i) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                aria-current={pathname === link.href ? "page" : undefined}
                className={cn(
                  "rounded-2xl px-4 py-3 text-2xl font-medium transition-colors duration-200",
                  "animate-in fade-in slide-in-from-bottom-4 fill-mode-both",
                  pathname === link.href
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-muted",
                )}
                style={{ animationDelay: `${i * 75}ms`, animationDuration: "400ms" }}
              >
                {link.label}
              </Link>
            ))}

            <Button
              asChild
              size="lg"
              className="mt-4 animate-in fade-in slide-in-from-bottom-4 fill-mode-both rounded-full active:scale-[0.97]"
              style={{
                animationDelay: `${(navLinks.length + mobileOnlyLinks.length) * 75}ms`,
                animationDuration: "400ms",
              }}
            >
              <Link href="/contact" onClick={() => setOpen(false)}>
                Contact
                <ArrowRight className="ml-1 size-4" />
              </Link>
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
